import {
  types,
  getParent,
  type Instance,
  type IAnyStateTreeNode,
  type ReferenceIdentifier,
} from 'mobx-state-tree';

import State, { type IState } from './state';
import TransitionMethod, { type ITransitionMethod } from './transition_method';
import type { IFSM } from './fsm';
import type { IObjectType } from './object_type';
import type { IMethod } from './method';

/**
 * The Transition instance interface.
 *
 * HAND-DECLARED (not `Instance<typeof Transition>`) to anchor the recursion:
 * Transition's `get fsm()` returns `IFSM`, FSM embeds a `Transition` array, so
 * deriving this from the model would loop the two through each other.
 */
export interface ITransition {
  readonly id: string;
  readonly name: string;
  readonly sourceState: IState;
  readonly targetState: IState;
  readonly methods: ITransitionMethod[];
  asString(): string;
  readonly displayString: string;
  readonly fsm: IFSM;
  readonly objectType: IObjectType;
  findMethod(method: IMethod): ITransitionMethod | undefined;
  readonly metaMethods: IMethod[];
  readonly metaMethodIds: string[];
  hasMethod(method: IMethod): boolean;
  setMethods(methods: Instance<typeof TransitionMethod>[]): void;
}

// Reference a State within the FSM.
function stateReference() {
  return types.reference(State, {
    get(identifier: ReferenceIdentifier, parent: IAnyStateTreeNode | null) {
      // parent is the transition
      const fsm = (parent as ITransition).fsm;
      // getStateById may return undefined for a dangling id; MST's get contract
      // expects the resolved instance, so we cast at the boundary (see refHelper).
      return fsm.getStateById(identifier as string) as IState;
    },
    set(value: IState) {
      return value.id;
    },
  });
}

/**
 * A MERODE FSM transition
 */
const Transition = types
  .model('Transition', {
    id: types.identifier,
    name: types.string,
    sourceState: stateReference(),
    targetState: stateReference(),
    methods: types.array(TransitionMethod),
  })
  .views((self0) => {
    const self = self0 as unknown as ITransition;
    return {
    // Human-readable label, e.g. for `${transition}` interpolation in messages.
    // (MST 7 forbids declaring a view named `toString`, so this is a plain view.)
    asString() {
      return self.name;
    },
    // Used in the logger and the report view
    get displayString() {
      const fsmDesc = `${self.objectType.name}.${self.fsm.name}`;
      const sourceDesc = `${fsmDesc}.${self.sourceState.name}`;
      const targetDesc = `${fsmDesc}.${self.targetState.name}`;
      return `Transition ${sourceDesc}-${targetDesc}`;
    },
    // Convenience method to get the FSM
    get fsm(): IFSM {
      return getParent(self0, 2);
    },
    // Convenience method to get the object type
    get objectType(): IObjectType {
      return self.fsm.objectType;
    },
    // find the transition method for the given method
    findMethod(method: IMethod) {
      return self.methods.find((m) => m.method === method);
    },
    // get the meta methods (i.e. the ones of type "Method"). In a consistent
    // model every transition method resolves, so the entries are non-null.
    get metaMethods(): IMethod[] {
      return self.methods.map((m) => m.method) as IMethod[];
    },
    // get the ids of the (meta)methods
    get metaMethodIds() {
      return self.metaMethods.map((m) => m.id);
    },
    // check wether this transition has the given method
    hasMethod(method: IMethod) {
      return self.metaMethods.includes(method);
    },
    };
  })
  .actions((self) => ({
    setMethods(methods: Instance<typeof TransitionMethod>[]) {
      self.methods = methods as typeof self.methods;
    },
  }));

export default Transition;
